import React, { useEffect, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  PlusIcon,
  XMarkIcon,
  DevicePhoneMobileIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { usePWAInstall } from '../context/PWAInstallContext';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Install PawaSchool popup.
 *
 * - Appears as a floating card on /login and /app/* (controlled by
 *   PWAInstallGate). Hidden on landing/apply/subscribe/etc.
 * - Shows the download icon + short pitch + Install button.
 * - Dismissible. Remembers dismissal for 7 days via localStorage.
 * - Auto-hides when the app is already installed.
 * - iOS Safari: shows the "how to install" modal instead (no
 *   beforeinstallprompt event on iOS).
 */
const InstallPWAButton = () => {
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [visible, setVisible] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [installing, setInstalling] = useState(false);

  // Decide whether to show, based on install state + dismissal memory.
  useEffect(() => {
    if (isInstalled) {
      setVisible(false);
      return;
    }
    if (!canInstall && !isIOS) {
      setVisible(false);
      return;
    }

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const cooledDown =
      !dismissedAt || Date.now() - dismissedAt > DISMISS_COOLDOWN_MS;

    if (cooledDown) {
      // Small delay so it slides in gracefully after the page settles.
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, [canInstall, isInstalled, isIOS]);

  if (isInstalled || !visible) return null;
  if (!canInstall && !isIOS) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const handleInstall = async () => {
    if (canInstall) {
      setInstalling(true);
      try {
        await promptInstall();
      } finally {
        setInstalling(false);
        setVisible(false);
      }
    } else if (isIOS) {
      setShowIOSModal(true);
    }
  };

  return (
    <>
      {/* ============= Popup card ============= */}
      <div
        className="
          fixed z-[90] print:hidden
          bottom-4 left-4 right-4
          sm:left-auto sm:right-5 sm:bottom-5 sm:w-[360px]
          animate-[slideUp_0.35s_ease-out]
        "
        role="dialog"
        aria-label="Install PawaSchool app"
      >
        <div className="relative rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden">
          {/* Gradient accent bar */}
          <div className="h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500" />

          {/* Close button */}
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="
              absolute top-3 right-3
              p-1.5 rounded-full
              text-slate-400 hover:text-slate-600 hover:bg-slate-100
              transition-colors
            "
          >
            <XMarkIcon className="w-4 h-4" />
          </button>

          <div className="p-4 pr-10 flex items-start gap-3">
            {/* Download icon in gradient badge */}
            <div
              className="
                flex-shrink-0 w-12 h-12 rounded-xl
                bg-gradient-to-br from-primary-500 to-secondary-500
                flex items-center justify-center
                shadow-md shadow-primary-500/20
              "
            >
              <ArrowDownTrayIcon className="w-6 h-6 text-white" />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0 pt-0.5">
              <div className="flex items-center gap-1.5">
                <SparklesIcon className="w-3.5 h-3.5 text-primary-500" />
                <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-600">
                  Available now
                </p>
              </div>
              <h3 className="mt-0.5 text-sm font-bold text-slate-800">
                Install PawaSchool
              </h3>
              <p className="mt-0.5 text-xs text-slate-500 leading-snug">
                Get the app on your device for faster access and offline
                support.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="px-4 pb-4 flex items-center gap-2">
            <button
              type="button"
              onClick={handleInstall}
              disabled={installing}
              className="
                flex-1 inline-flex items-center justify-center gap-2
                px-4 py-2.5 rounded-xl
                bg-gradient-to-r from-primary-600 to-primary-500
                text-white text-sm font-semibold
                shadow-sm hover:shadow-md
                hover:from-primary-700 hover:to-primary-600
                transition-all
                disabled:opacity-60 disabled:cursor-not-allowed
              "
            >
              {installing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Installing...
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  Install App
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleDismiss}
              className="
                px-3 py-2.5 rounded-xl
                text-sm font-medium text-slate-500
                hover:bg-slate-100 hover:text-slate-700
                transition-colors
              "
            >
              Not now
            </button>
          </div>

          {/* Small hint footer */}
          <div
            className="
              px-4 py-2 border-t border-slate-100
              flex items-center gap-1.5
              text-[10px] text-slate-400
            "
          >
            <DevicePhoneMobileIcon className="w-3 h-3" />
            <span>Works on phones, tablets and desktop</span>
          </div>
        </div>
      </div>

      {/* ============= iOS how-to modal ============= */}
      {showIOSModal && (
        <div
          className="fixed inset-0 bg-black/50 z-[100] flex items-end sm:items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setShowIOSModal(false)}
        >
          <div
            className="
              bg-white rounded-2xl max-w-md w-full shadow-2xl
              overflow-hidden animate-[slideUp_0.3s_ease-out]
            "
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient */}
            <div className="relative h-24 bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
              <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                <ArrowDownTrayIcon className="w-8 h-8 text-white" />
              </div>
              <button
                type="button"
                onClick={() => setShowIOSModal(false)}
                className="
                  absolute top-3 right-3
                  p-1.5 rounded-full
                  bg-white/20 hover:bg-white/30
                  text-white transition-colors
                "
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <h3 className="text-lg font-bold text-slate-800 text-center">
                Install PawaSchool
              </h3>
              <p className="text-sm text-slate-500 text-center mt-1">
                Add it to your Home Screen for a full-screen app experience
              </p>

              <ol className="mt-6 space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                    1
                  </span>
                  <div className="text-sm text-slate-700 pt-1">
                    Tap the{' '}
                    <span className="inline-flex items-center gap-1 font-semibold text-primary-600">
                      <ArrowUpTrayIcon className="w-4 h-4" /> Share
                    </span>{' '}
                    button at the bottom of Safari.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                    2
                  </span>
                  <div className="text-sm text-slate-700 pt-1">
                    Scroll and tap{' '}
                    <span className="inline-flex items-center gap-1 font-semibold text-primary-600">
                      <PlusIcon className="w-4 h-4" /> Add to Home Screen
                    </span>
                    .
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                    3
                  </span>
                  <div className="text-sm text-slate-700 pt-1">
                    Tap <strong>Add</strong> — PawaSchool will appear on your
                    Home Screen.
                  </div>
                </li>
              </ol>

              <button
                type="button"
                onClick={() => setShowIOSModal(false)}
                className="
                  mt-6 w-full py-3 rounded-xl
                  bg-gradient-to-r from-primary-600 to-primary-500
                  text-white font-semibold
                  hover:from-primary-700 hover:to-primary-600
                  transition-all shadow-sm hover:shadow-md
                "
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWAButton;